import PropTypes from 'prop-types';
import MultiselectGrouped from 'Components/Molecules/MultiselectGrouped/MultiselectGrouped';
import { cn } from 'Utils/cn';
import { forwardRef, useMemo, useState } from 'react';

function TaxesList(props) {
    const { taxes = [] } = props;

    return (
        <div className={cn('flex flex-wrap gap-1 min-w-0 max-w-full h-full items-center')}>
            {taxes.map((tax) => (
                <div
                    key={tax.id}
                    className="text-xs font-medium whitespace-nowrap text-gray-700 bg-gray-100 rounded px-2 py-0.5 max-w-[160px] overflow-hidden text-ellipsis"
                >
                    {tax.name}
                </div>
            ))}
        </div>
    );
}

TaxesList.propTypes = {
    taxes: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            group: PropTypes.string.isRequired,
            group_name: PropTypes.string.isRequired,
            country: PropTypes.string.isRequired,
            country_region: PropTypes.string,
            percentage: PropTypes.number.isRequired,
            is_active: PropTypes.bool.isRequired,
            is_retention: PropTypes.bool.isRequired,
        }),
    ).isRequired,
};

const MultiselectTaxesMultiple = forwardRef(function MultiselectTagsMultipleTaxonomies(props, ref) {
    const { value, onChange, label, disabled, error, className, highlight, actions, taxes } = props;
    const retentions = useMemo(() => {
        return taxes.filter((t) => t.is_retention);
    }, [taxes]);
    const ivas = useMemo(() => {
        return taxes.filter((t) => !t.is_retention);
    }, [taxes]);
    const [retention, setRetention] = useState(() => {
        if (!Array.isArray(value)) return null;
        const retentionId = retentions.find((retentionTax) => {
            return value.includes(retentionTax.id);
        })?.id;
        return retentionId || null;
    });
    const [iva, setIva] = useState(() => {
        if (!Array.isArray(value)) return null;
        const ivaId = ivas.find((ivaTax) => {
            return value.includes(ivaTax.id);
        })?.id;
        return ivaId || null;
    });
    const taxesByGroup = Object.values(
        taxes.reduce((acc, tax) => {
            if (!acc[tax.group]) {
                acc[tax.group] = {
                    groupId: tax.group,
                    label: tax.group_name,
                    items: [],
                };
            }
            acc[tax.group].items.push({
                ...tax,
                label: tax.name,
            });
            return acc;
        }, {}),
    );
    const optionsSelected = Array.isArray(value) ? value : [];
    const hasSelected = optionsSelected.length > 0;
    const handleChange = (selectedIds, isAdded) => {
        if (isAdded) {
            const newId = selectedIds.find((id) => id !== retention && id !== iva);
            if (retentions.find((t) => t.id === newId)) {
                onChange([newId, iva].filter(Boolean));
                setRetention(newId);
            } else if (ivas.find((t) => t.id === newId)) {
                onChange([newId, retention].filter(Boolean));
                setIva(newId);
            }
        } else {
            const removedId = optionsSelected.find((id) => !selectedIds.includes(id));
            if (retentions.find((t) => t.id === removedId)) {
                setRetention(null);
                onChange([iva].filter(Boolean));
            } else if (ivas.find((t) => t.id === removedId)) {
                setIva(null);
                onChange([retention].filter(Boolean));
            }
        }
    };
    return (
        <MultiselectGrouped
            ref={ref}
            disabled={disabled}
            enableClear={hasSelected}
            onClear={() => onChange([])}
            floatLabel={label}
            label={<TaxesList taxes={taxes.filter((tax) => optionsSelected.includes(tax.id))} />}
            options={taxesByGroup}
            optionsSelected={optionsSelected}
            onChangeSelected={handleChange}
            className={cn('h-auto min-h-11 py-2', className)}
            error={error}
            highlight={highlight}
            actions={actions}
        />
    );
});

export default MultiselectTaxesMultiple;

MultiselectTaxesMultiple.propTypes = {
    disabled: PropTypes.bool,
    value: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    className: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    highlight: PropTypes.bool,
    onAdd: PropTypes.func,
    actions: PropTypes.arrayOf(PropTypes.node),
    taxes: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            group: PropTypes.string.isRequired,
            group_name: PropTypes.string.isRequired,
            country: PropTypes.string.isRequired,
            country_region: PropTypes.string,
            percentage: PropTypes.number.isRequired,
            is_active: PropTypes.bool.isRequired,
            is_retention: PropTypes.bool.isRequired,
        }),
    ).isRequired,
};
